import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabPanel, TabView } from 'primereact/tabview';
import { Toast } from 'primereact/toast';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import ChangePassword from '../component/employee/change-password';
import EmployeeUpdate from '../component/employee/update';
import TableParents from '../component/parent/table';
import TableServicesGroup from '../component/service/group/table';
import TableServicesIndividual from '../component/service/individual/table';
import TableServicesProgram from '../component/service/program/table';
import { useEmployee } from '../context/employee';
import { subscribe, unsubscribe } from '../context/events';
import { MSG } from '../shared';

function EmployeePage() {
    const toast = useRef(null);

    const { id } = useParams();

    const { employees, employee: user } = useEmployee();

    // NOTE: employee is the data for this page and user is the currently logged in employee
    const [initialized, setInitialized] = useState(false);
    const [employee, setEmployee] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [openUpdate, setOpenUpdate] = useState(false);
    const [openChangePassword, setOpenChangePassword] = useState(false);

    const TitleDisplay = () => {
        return (
            <span
                className={employee.active ? '' : 'text-red-500'}
                onClick={() => {
                    setOpenUpdate(true);
                }}
            >
                {employee.name}'s Profile Page
            </span>
        );
    }

    const NotesDisplay = () => {
        return (
            <span>
                <span className="font-semibold">Notes</span>
                <br />
                <span dangerouslySetInnerHTML={{ __html: employee.notes }}></span>
            </span>
        );
    };

    const AddressDisplay = () => {
        return (
            <span>
                <span className="font-semibold">Address</span>

                <br />

                {!employee.streetAddress1 ? '' : employee.streetAddress1}
                {!employee.streetAddress1 ? '' : <br />}

                {!employee.streetAddress2 ? '' : employee.streetAddress2}
                {!employee.streetAddress2 ? '' : <br />}

                {!employee.city ? '' : employee.city}
                {!employee.city ? '' : ', '}

                {!employee.state.itemData.abbr ? '' : employee.state.itemData.abbr}
                {!employee.state.itemData.abbr ? '' : ' '}

                {!employee.zipCode ? '' : employee.zipCode}

                <br />
                <br />
            </span>
        );
    };

    const canChangePassword = useCallback(() => {
        if (!!user.admin) {
            return true;
        }

        if (employee._id === user._id) {
            return true;
        }

        return false;
    }, [employee, user]);

    // App Events Method
    const processEvent = useCallback(
        (event) => {
            const { detail: res, type: action } = event;

            if (res.status === 200) {
                if (action === MSG.update.employee && res.data.employee._id === employee._id) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Employee Updated',
                        detail: 'Updated automatically',
                        life: 3000
                    });

                    setEmployee(res.data.employee);
                }
            }
        },
        [employee]
    );

    // App Events Effect
    useEffect(() => {
        subscribe(MSG.update.employee, processEvent);

        return () => {
            unsubscribe(MSG.update.employee, processEvent);
        };
    }, [processEvent]);

    // Init Effect
    useEffect(() => {
        if (employees.length === 0) {
            return;
        }

        if (initialized) {
            return;
        }

        const _employee = employees.find((e) => e.username === id);

        setEmployee(_employee ? _employee : null);
        setInitialized(true);
    }, [employees, id, initialized]);

    if (!initialized) {
        return (
            <Card title="Employee Dashboard" subTitle="Initializing" className="pageCard">
                <ProgressSpinner />
            </Card>
        );
    }

    if (!employee) {
        return (
            <Card
                title="Employee Dashboard"
                subTitle={`Invalid username: ${id}`}
                className="pageCard"
            >
                <p>Invalid username for employee, please use the nav bar to go somewhere else</p>
            </Card>
        );
    }

    return (
        <Card title={TitleDisplay} className="pageCard">
            <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
                <TabPanel header="Info">
                    <div className="card flex flex-wrap gap-3 p-fluid">
                        <div className="flex-auto" style={{ maxWidth: '33vw' }}>
                            <span>
                                <span className="font-semibold">Username</span>
                                <br />
                                {employee.username}
                            </span>

                            <span>
                                <br />
                                <br />
                                <span className="font-semibold">Admin</span>
                                <br />
                                {employee.admin ? 'Yes' : 'No'}
                            </span>

                            <span>
                                <br />
                                <br />
                                <span className="font-semibold">Job Title</span>
                                <br />
                                {employee.jobTitle}
                            </span>

                            <span>
                                <br />
                                <br />
                                <span className="font-semibold">Work Email</span>
                                <br />
                                {employee.workEmail}
                            </span>

                            <span>
                                <br />
                                <br />
                                <span className="font-semibold">Work Ext</span>
                                <br />
                                {employee.workExt}
                            </span>
                        </div>

                        <div className="flex-auto" style={{ maxWidth: '33vw' }}>
                            <span>
                                <span className="font-semibold">Home Email</span>
                                <br />
                                {employee.homeEmail}
                            </span>

                            <span>
                                <br />
                                <br />
                                <span className="font-semibold">Home Phone</span>
                                <br />
                                {employee.homePhone}
                            </span>

                            <br />
                            <br />
                            <AddressDisplay />
                        </div>

                        <div className="flex-auto" style={{ maxWidth: '33vw' }}>
                            {activeIndex !== 0 ? null : (
                                <>
                                    {!canChangePassword() ? null : (
                                        <Button
                                            label="Change Password"
                                            severity="help"
                                            onClick={() => {
                                                setOpenChangePassword(true);
                                            }}
                                        />
                                    )}
                                </>
                            )}

                            <span>
                                <br />
                                <br />
                                <span className="font-semibold">Emergency Contact Name</span>
                                <br />
                                {employee.emergencyContactName}
                            </span>

                            <span>
                                <br />
                                <br />
                                <span className="font-semibold">Emergency Contact Phone</span>
                                <br />
                                {employee.emergencyContactPhone}
                            </span>

                            <span>
                                <br />
                                <br />
                                <span className="font-semibold">
                                    Emergency Contact Relationship
                                </span>
                                <br />
                                {employee.emergencyContactRelationship.itemName}
                            </span>

                            <br />
                            <br />
                            <NotesDisplay />
                        </div>
                    </div>
                </TabPanel>

                <TabPanel header={`Parents Added: ${employee.countParentsAdded}`}>
                    {activeIndex !== 1 ? null : (
                        <TableParents initFilters={[{ field: 'addedBy', value: employee._id }]} />
                    )}
                </TabPanel>

                <TabPanel header={`Group Services: ${employee.countServicesGroup}`}>
                    {activeIndex !== 2 ? null : (
                        <>
                            <TableServicesGroup
                                initFilters={[{ field: 'employees', value: [employee._id] }]}
                            />
                        </>
                    )}
                </TabPanel>

                <TabPanel header={`Individual Services: ${employee.countServicesIndividual}`}>
                    {activeIndex !== 3 ? null : (
                        <>
                            <TableServicesIndividual
                                initFilters={[{ field: 'employee', value: employee._id }]}
                            />
                        </>
                    )}
                </TabPanel>

                <TabPanel header={`Program Services: ${employee.countServicesProgram}`}>
                    {activeIndex !== 4 ? null : (
                        <>
                            <TableServicesProgram
                                initFilters={[{ field: 'employee', value: employee._id }]}
                            />
                        </>
                    )}
                </TabPanel>
            </TabView>

            {!openChangePassword ? null : (
                <ChangePassword
                    open={openChangePassword}
                    employee={employee}
                    onClose={(res) => {
                        setOpenChangePassword(false);

                        if (res.status === 200) {
                            toast.current.show({
                                severity: 'success',
                                summary: 'Success',
                                detail: `Successfully updated password for ${employee.username}`,
                                life: 3000
                            });
                        } else {
                            toast.current.show({
                                severity: 'danger',
                                summary: 'Failure',
                                detail: `Failed to update password for ${employee.username}. ${res.message}`,
                                life: 3000
                            });
                        }
                    }}
                />
            )}

            {!openUpdate ? null : (
                <EmployeeUpdate
                    open={openUpdate}
                    data={employee}
                    onClose={(res) => {
                        if (res.status === 200) {
                            toast.current.show({
                                severity: 'success',
                                summary: 'Success',
                                detail: `Successfully Updated ${res.data.employee.username}`,
                                life: 3000
                            });
                        }

                        setOpenUpdate(false);
                    }}
                />
            )}

            <Toast ref={toast} />
        </Card>
    );
}

export default EmployeePage;
